import type { RenderInput } from '@str/shared';
import type { ReelDraft } from './reel';
export type ReelClip = NonNullable<RenderInput['clips']>[number];
export const sequenceDuration = (clips: ReelClip[]) =>
  clips.reduce((sum, clip) => sum + clip.trim.endMs - clip.trim.startMs, 0);
export function effectiveClips(draft: ReelDraft): ReelClip[] {
  return draft.clips ?? [{ id: 'original', source: { kind: 'original' }, trim: draft.trim }];
}
export function enterSequence(draft: ReelDraft): ReelDraft {
  if (draft.clips) return draft;
  return {
    ...draft,
    clips: effectiveClips(draft),
    trim: { startMs: 0, endMs: draft.trim.endMs - draft.trim.startMs },
    coverMs: Math.max(0, draft.coverMs - draft.trim.startMs),
  };
}
export function outputPoint(clips: ReelClip[], outputMs: number) {
  let offset = 0;
  for (let index = 0; index < clips.length; index++) {
    const clip = clips[index]!;
    const length = clip.trim.endMs - clip.trim.startMs;
    if (outputMs < offset + length || index === clips.length - 1)
      return {
        index,
        sourceMs: clip.trim.startMs + Math.max(0, Math.min(length - 1, outputMs - offset)),
        offset,
      };
    offset += length;
  }
  return { index: 0, sourceMs: 0, offset: 0 };
}
export function reconcileSequence(draft: ReelDraft, clips: ReelClip[]) {
  const total = sequenceDuration(clips);
  const coverMs = Math.max(0, Math.min(total - 1, draft.coverMs));
  const layerTimings = draft.layerTimings?.map((t) => ({
    ...t,
    startMs: Math.min(t.startMs, total - 1),
    endMs: Math.min(t.endMs, total),
  }));
  const adjusted =
    coverMs !== draft.coverMs ||
    JSON.stringify(layerTimings) !== JSON.stringify(draft.layerTimings);
  return {
    draft: {
      ...draft,
      clips,
      trim: { startMs: 0, endMs: total },
      coverMs,
      ...(layerTimings ? { layerTimings } : {}),
    },
    notice: adjusted ? 'Cover and text times were adjusted to fit the new length.' : '',
  };
}
export function fadeOpacity(
  clips: ReelClip[],
  outputMs: number,
  transition?: RenderInput['transition'],
) {
  if (transition !== 'fade') return 1;
  const { index, offset } = outputPoint(clips, outputMs);
  const clip = clips[index];
  if (!clip) return 1;
  const local = outputMs - offset,
    length = clip.trim.endMs - clip.trim.startMs;
  return Math.max(
    0,
    Math.min(1, index > 0 ? local / 250 : 1, index < clips.length - 1 ? (length - local) / 250 : 1),
  );
}
